import { Lightning, Router } from '@lightningjs/sdk'

import {
  getColor,
  grey400,
  grey50,
  grey700,
  grey800,
  grey960,
  primary500,
  transparent,
} from '../../constants/colors'
import { UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import {
  ADD_FAVORITES_ENTITY_SEARCH,
  ADD_FAVORITES_SPORT_PICKER,
  COLLECTION,
} from '../../constants/routes'
import { fetchData } from '../../services/flosports/floExperience'
import { TvButtonModel } from '../../types/flo-experience'
import { getText } from '../../utils/dictionary'
import { getIcon } from '../../utils/image'
import { captureClick, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import SpeechComp from '../speechComp/speechComp'

enum EXPERIENCES {
  ADD_FAVORITES = 'add-favorites',
  SELECT_SPORT = 'select-sport',
  SPORTS = 'sports',
}
const ICON_SIZE = 32

interface ButtonColorOverride {
  fillFocused?: number
  labelFocused?: number
  fillUnfocused?: number
  labelUnfocused?: number
  accent?: number
}

export default class Button extends SpeechComp {
  private _data: TvButtonModel
  private _isTransparent = false
  private _isBtnEnabled = true
  private _padding = 32
  private _adjustWidthToContent = false
  private _withIcon = false
  private _rowIndex = 0
  private _buttonIndex = 0
  private _colorOverride: ButtonColorOverride

  static _template() {
    return {
      w: 320,
      h: 84,
      rect: true,
      clipping: true,
      collision: true,
      color: getColor(primary500),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BORDER_RADIUS,
      },
      Fill: {
        x: -1,
        y: -1,
        w: (w) => w + 2,
        h: (h) => h + 2,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
          fillColor: getColor(grey700),
        },
      },
      Wrapper: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        },
        Icon: {
          w: 36,
          h: 36,
          color: getColor(grey50),
          flexItem: { marginRight: 24 },
          texture: null,
          visible: false,
        },
        Label: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontSize: 32,
            fontFace: UniNeueRegular,
            lineHeight: 32,
            verticalAlign: 'middle',
          },
        },
      },
    }
  }

  get wrapper() {
    return this.tag('Wrapper')
  }

  get fill() {
    return this.tag('Fill')
  }

  set colorOverride(override: ButtonColorOverride) {
    this._colorOverride = override

    if (override?.accent !== null && override.accent >= 0) {
      this.patch({ color: override.accent })
    }
  }

  set adjustWidthToContent(v: boolean) {
    this._adjustWidthToContent = v
  }

  get adjustWidthToContent() {
    return this._adjustWidthToContent
  }

  get label() {
    return this.tag('Label')
  }

  set label(v) {
    this.label.patch({ text: v })
  }

  get icon() {
    return this.tag('Icon')
  }

  set icon(v) {
    this._withIcon = true

    this.icon.patch({
      visible: true,
      texture: Lightning.Tools.getSvgTexture(getIcon(v.toString()), ICON_SIZE, ICON_SIZE),
    })
  }

  get withIcon() {
    return this._withIcon
  }

  set isTransparent(v) {
    this._isTransparent = v
  }

  set isEnabled(v) {
    this._isBtnEnabled = v
    this.updateUnfocusedColors()
  }

  get isTransparent() {
    return this._isTransparent
  }

  set data(v: TvButtonModel) {
    this._data = v
  }

  get data() {
    return this._data
  }

  set rowIndex(v) {
    this._rowIndex = v
  }

  get rowIndex() {
    return this._rowIndex
  }

  get speechText(): string {
    return this.label.text.text
  }

  set buttonIndex(v) {
    this._buttonIndex = v
  }

  get buttonIndex() {
    return this._buttonIndex
  }

  get padding() {
    return this._padding
  }

  set padding(v: number) {
    this._padding = v
  }

  _init() {
    this.updateUnfocusedColors()

    this.label.on('txLoaded', () => {
      if (this.adjustWidthToContent) {
        const iconWidth = this.withIcon ? ICON_SIZE : 0
        this.w = this.label.renderWidth + this.padding * 2 + iconWidth
      }
    })
  }

  _active() {
    if (this.data) {
      this.renderButtonData()
    }
  }

  _handleEnter() {
    void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, this.data?.action?.analytics, {
      item_position: this.buttonIndex,
      row_number: this.buttonIndex,
    })

    if (this.data?.action?.api_call) {
      this.actOnApiCall()
    } else if (this.data?.action?.url) {
      const actionUrl = this.data.action.url

      if (actionUrl.includes('authentication-required')) {
        this.fireAncestors('$fireAuthRequired', getText('authenticationRequiredInfo'))
      } else {
        const encodedUrl = encodeURIComponent(actionUrl)

        let finalUrl
        if (actionUrl.includes(EXPERIENCES.ADD_FAVORITES)) {
          const action = actionUrl.includes(EXPERIENCES.SPORTS) ? 'add' : 'select'
          const sportPickerPage = ADD_FAVORITES_SPORT_PICKER.replace(':action', action).replace(
            ':url',
            encodedUrl,
          )
          const searchEntityPage = ADD_FAVORITES_ENTITY_SEARCH.replace(':url', encodedUrl)
          finalUrl =
            actionUrl.includes(EXPERIENCES.SPORTS) || actionUrl.includes(EXPERIENCES.SELECT_SPORT)
              ? sportPickerPage
              : searchEntityPage
        } else {
          finalUrl = COLLECTION.replace(':url', encodedUrl)
        }

        Router.navigate(finalUrl)
      }
    } else {
      // Bubbles up the event so the parent component can act on it
      return false
    }
  }

  _focus() {
    this.focusButton(true)
  }

  _unfocus() {
    this.focusButton(false)
  }

  async actOnApiCall() {
    const response = await fetchData(this.data.action.api_call)
    this.fireAncestors('$onApiCall', response, this.data)
  }

  getUnfocusColor() {
    const unfocusColor = this._isBtnEnabled
      ? this.getColorOverride(this._colorOverride?.fillUnfocused, getColor(grey700))
      : getColor(grey800)
    return this._isTransparent ? transparent : unfocusColor
  }

  getUnfocusTextColor() {
    return this._isBtnEnabled
      ? this.getColorOverride(this._colorOverride?.labelUnfocused, getColor(grey50))
      : getColor(grey400)
  }

  updateUnfocusedColors() {
    if (this._isTransparent) {
      this.patch({
        color: transparent,
      })
    }

    this.fill.patch({
      x: -1,
      w: (w) => w + 2,
      shader: { fillColor: this.getUnfocusColor() },
    })
    this.wrapper.patch({
      Icon: {
        color: this.getUnfocusTextColor(),
      },
      Label: {
        text: {
          textColor: this.getUnfocusTextColor(),
        },
      },
    })
  }

  focusButton(isFocused: boolean) {
    const bgColor = isFocused
      ? this.getColorOverride(this._colorOverride?.fillFocused, getColor(grey50))
      : this.getUnfocusColor()
    const color = isFocused
      ? this.getColorOverride(this._colorOverride?.labelFocused, getColor(grey960))
      : this.getUnfocusTextColor()

    if (this._isTransparent) {
      this.patch({
        color: isFocused
          ? this.getColorOverride(this._colorOverride?.accent, getColor(primary500))
          : transparent,
      })
    }

    this.fill.patch({
      x: isFocused ? 6 : -1,
      w: isFocused ? (w) => w - 5 : (w) => w + 2,
      shader: { fillColor: bgColor },
    })
    this.wrapper.patch({
      Icon: {
        color: color,
      },
      Label: {
        text: {
          textColor: color,
        },
      },
    })

    if (isFocused) {
      this.speak()
    }
  }

  renderButtonData() {
    this.wrapper.patch({
      ...(this.data?.icon && {
        Icon: {
          visible: true,
          texture: Lightning.Tools.getSvgTexture(
            getIcon(this.data?.icon?.style),
            ICON_SIZE,
            ICON_SIZE,
          ),
        },
      }),
      Label: {
        text: {
          text: this.data?.title?.text ?? '',
        },
      },
    })
  }

  getColorOverride(override: number, original: any) {
    if (override !== null && override >= 0) {
      return override
    }

    return original ?? undefined
  }

  _handleHover() {
    if (this._isBtnEnabled) {
      this.fireAncestors('$updateFocusByHover', this)
      this._focus()
    }
  }

  _handleUnhover() {
    if (this._isBtnEnabled) {
      this._unfocus()
    }
  }

  _handleClick() {
    if (this._isBtnEnabled) {
      return this._handleEnter()
    }
  }
}
